import type {
  CanaryAuthMode,
  CanaryHistoryMappingInput,
  ExternalHistoryProvider,
  ExternalHistorySource,
  PIHistoryMappingInput
} from '@axilon-apps/domains-ontology-ui-state'

import type { PrepValues, SubOptionId, WizardPauseReason } from './types'

export type { ExternalHistoryProvider, ExternalHistorySource }

/** Operator-facing vocabulary per external historian. */
export const EXTERNAL_HISTORY_LABELS: Record<
  ExternalHistoryProvider,
  { historian: string; record: string; records: string }
> = {
  pi: { historian: 'PI', record: 'PI point', records: 'PI points' },
  canary: { historian: 'Canary', record: 'Canary tag', records: 'Canary tags' }
}

/** Both the generalized reason and the pre-rename PI spelling pause for mapping approval. */
export function isExternalHistoryMappingPause(
  reason: WizardPauseReason | null | undefined
): boolean {
  return reason === 'external_history_mapping' || reason === 'pi_mapping'
}

export function parseExternalHistorySources(value: string | undefined): ExternalHistorySource[] {
  if (value === undefined) return ['ignition', 'opc']
  try {
    const parsed: unknown = JSON.parse(value)
    if (!Array.isArray(parsed)) return []
    return parsed.filter(
      (source): source is ExternalHistorySource => source === 'ignition' || source === 'opc'
    )
  } catch {
    return []
  }
}

export function canaryAuthModeFor(sub: SubOptionId | undefined): CanaryAuthMode {
  return sub === 'user' || sub === 'anonymous' ? sub : 'api_token'
}

/** The credential the historian needs from the prepared fields, keyed on provider + auth mode. */
export interface ExternalHistoryCredentials {
  authMode?: CanaryAuthMode
  username?: string
  password?: string
  apiToken?: string
}

export function externalHistoryCredentials(
  provider: ExternalHistoryProvider,
  prep: Pick<PrepValues, 'fields'>,
  sub?: SubOptionId
): ExternalHistoryCredentials {
  if (provider === 'pi') {
    return { username: prep.fields.username ?? '', password: prep.fields.password ?? '' }
  }
  const authMode = canaryAuthModeFor(sub)
  return {
    authMode,
    ...(authMode === 'api_token' ? { apiToken: prep.fields.apiToken ?? '' } : {}),
    ...(authMode === 'user'
      ? { username: prep.fields.username ?? '', password: prep.fields.password ?? '' }
      : {})
  }
}

export function externalHistoryCredentialsReady(
  provider: ExternalHistoryProvider,
  credentials: ExternalHistoryCredentials
): boolean {
  if (provider === 'pi') return Boolean(credentials.username && credentials.password)
  switch (credentials.authMode) {
    case 'api_token':
      return Boolean(credentials.apiToken)
    case 'user':
      return Boolean(credentials.username && credentials.password)
    default:
      return true
  }
}

/**
 * Secrets are never returned to the browser after a refresh, so a paused run
 * may need the operator to re-enter the one field the historian requires
 * before the mapping studio can probe again. Null when nothing is missing.
 */
export function externalHistoryMissingCredential(
  provider: ExternalHistoryProvider,
  credentials: ExternalHistoryCredentials
): { field: 'password' | 'apiToken'; label: string } | null {
  if (externalHistoryCredentialsReady(provider, credentials)) return null
  if (provider === 'canary' && credentials.authMode === 'api_token') {
    return { field: 'apiToken', label: 'Re-enter the Canary API token to review mappings' }
  }
  return {
    field: 'password',
    label: `Re-enter the ${EXTERNAL_HISTORY_LABELS[provider].historian} password to review mappings`
  }
}

export interface ExternalHistoryRequestInputs {
  provider: ExternalHistoryProvider
  siteId: string
  branchId?: string
  groupId?: string
  baseUrl: string
  skipTlsVerify: boolean
  nameFilter?: string
  rootPath?: string
  credentials: ExternalHistoryCredentials
  historySources: ExternalHistorySource[]
}

/** The suggest / preview request body for one provider, from the wizard's prepared values. */
export function buildExternalHistoryMappingRequest(
  inputs: ExternalHistoryRequestInputs
): PIHistoryMappingInput | CanaryHistoryMappingInput {
  const shared = {
    baseUrl: inputs.baseUrl,
    siteId: inputs.siteId,
    skipTlsVerify: inputs.skipTlsVerify,
    historySources: inputs.historySources,
    ...(inputs.nameFilter?.trim() ? { nameFilter: inputs.nameFilter.trim() } : {}),
    ...(inputs.branchId ? { branchId: inputs.branchId } : {}),
    ...(inputs.groupId ? { groupId: inputs.groupId } : {})
  }
  if (inputs.provider === 'pi') {
    return {
      ...shared,
      username: inputs.credentials.username ?? '',
      password: inputs.credentials.password ?? ''
    }
  }
  const authMode = inputs.credentials.authMode ?? 'api_token'
  return {
    ...shared,
    authMode,
    ...(authMode === 'api_token' ? { apiToken: inputs.credentials.apiToken ?? '' } : {}),
    ...(authMode === 'user'
      ? {
          username: inputs.credentials.username ?? '',
          password: inputs.credentials.password ?? ''
        }
      : {}),
    ...(inputs.rootPath?.trim() ? { rootPath: inputs.rootPath.trim() } : {})
  }
}
